/* eslint-disable react/prop-types */
import React, {
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { Row, Col } from 'antd';
import { BranchesOutlined, SettingOutlined } from '@ant-design/icons';

import WorkflowActionNode from './WorkflowActionNode';
import WorkflowHandle from './WorkflowHandle';
import BorderlessButton from '../../common/buttons/BorderlessButton';
import useToggle from '../../common/hooks/useToggle';
import WorkflowLogicalConditionNodeConfigureDrawer from './WorkflowLogicalConditionNodeConfigureDrawer';
import FormWorkflowContext from './FormWorkflowContext';

const HOVER_TEXT = `
Based on the condition set, the form will proceed to the next step of the workflow.
Leaving conditions empty in this step will result in the form not proceeding further.
`;

const DISABLED_TEXT = 'Add at least one Choose one or more field, Text, Attribute, Yes/No or Table Field `to the form to use this action.';

const ERROR_DETAILS = {
  content: (
    <div style={{ maxWidth: 300 }}>
      This step requires at least one <b>Condition</b> to be set via the <b>Configure</b> button.
    </div>
  ),
};

export default function WorkflowLogicalConditionNode({
  setElements,
  setDataMap,
  isDisplay,
  sections = [],
  customers = [],
  projects = [],
  users = [],
  costcodes = [],
  phases = [],
  projectIdMap = {},
  vendors = [],
  equipment = [],
  formTemplates = [],
  labels = [],
  contactAddressBooks = {},
  buckets = [],
  scheduleCategories = [],
  setDisableNext,
} = {}) {
  // eslint-disable-next-line
  return ({
    id,
    draggable,
    disabled,
    data = {},
  }) => {
    const {
      formulas = [],
    } = data;
    const [nodeFormulas, setNodeFormulas] = useState(formulas);
    const { toggle, isToggled } = useToggle();
    // React Flow caches the first nodeTypes component, so factory costcodes stay stale; read live values from context.
    const {
      costcodes: contextCostcodes,
      phases: contextPhases,
      projectIdMap: contextProjectIdMap,
    } = useContext(FormWorkflowContext) ?? {};
    const costcodesToUse = contextCostcodes ?? costcodes;
    const phasesToUse = contextPhases ?? phases;
    const projectIdMapToUse = contextProjectIdMap ?? projectIdMap;

    const hasValidFormula = nodeFormulas?.some((formula) => (
      formula.field && formula.operator && formula.value
    ));

    useEffect(() => {
      if (!setDisableNext) return;

      setDisableNext((prev) => ({ ...prev, [id]: !hasValidFormula }));
    }, [setDisableNext, id, hasValidFormula]);

    const onChange = useCallback((newFormulas) => {
      setDataMap((dataMap) => ({
        ...dataMap,
        [id]: { formulas: newFormulas },
      }));
      setNodeFormulas(newFormulas);
      toggle();
    }, [id, toggle]);

    const onClose = useCallback(() => {
      toggle();
      setNodeFormulas(nodeFormulas);
    }, [toggle, nodeFormulas]);

    return (
      <WorkflowActionNode
        id={id}
        title={`Logical Condition Check ${disabled ? ' - DISABLED' : ''}`}
        Icon={BranchesOutlined}
        type="logicalCondition"
        draggable={draggable}
        onNodeUpdate={(...args) => {
          setElements(...args);
          if (setDisableNext) {
            setDisableNext((prev) => ({ ...prev, [id]: false }));
          }
        }}
        isDisplay={isDisplay || disabled}
        hover={disabled ? DISABLED_TEXT : HOVER_TEXT}
        style={disabled ? { opacity: 0.7 } : {}}
        className="workflow-logic-node"
        errorPopover={!hasValidFormula ? ERROR_DETAILS : null}
      >
        {!draggable && (
        <div>
          <Row style={{ margin: '20px 0px' }}>
            <BorderlessButton
              title={formulas?.length ? `${formulas.length} Condition(s)` : 'Configure'}
              style={{ margin: 5, background: 'transparent' }}
              iconNode={<SettingOutlined />}
              onClick={toggle}
            />
          </Row>

          <Row justify="space-between" style={{ marginBottom: 5, marginLeft: 20, marginRight: 20 }}>
            <Col>
              True
            </Col>
            <Col>
              False
            </Col>
          </Row>

          <WorkflowHandle type="target" position="top" disabled={isDisplay} />

          <WorkflowHandle
            type="source"
            position="bottom"
            style={{ left: '20%' }}
            id={`${id}-logicalConditionTrue`}
            disabled={isDisplay}
          />
          <WorkflowHandle
            type="source"
            position="bottom"
            style={{ left: '80%' }}
            id={`${id}-logicalConditionFalse`}
            disabled={isDisplay}
          />
          <WorkflowLogicalConditionNodeConfigureDrawer
            formulas={nodeFormulas}
            sections={sections}
            customers={customers}
            projects={projects}
            users={users}
            costcodes={costcodesToUse}
            phases={phasesToUse}
            projectIdMap={projectIdMapToUse}
            vendors={vendors}
            equipment={equipment}
            formTemplates={formTemplates}
            labels={labels}
            contactAddressBooks={contactAddressBooks}
            scheduleCategories={scheduleCategories}
            buckets={buckets}
            onClose={onClose}
            onChange={setNodeFormulas}
            onSave={onChange}
            visible={isToggled}
          />
        </div>
        )}
      </WorkflowActionNode>
    );
  };
}
